import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../software/api";
import { toast } from "sonner";
import { X, Search, FileText, Trash2, Download, Inbox, Users, Truck, Building2, FolderClosed, Sparkles } from "lucide-react";
import { cellInp, HdrBtn } from "./client/shared";

const base = process.env.REACT_APP_BACKEND_URL;
const isImg = (ct) => (ct || "").startsWith("image/");

// target = { mode:'file', file:{...} }  OR  { mode:'folder', folder:{folder_name, top_type, count} }
function AssignPicker({ target, onAssigned, onClose }) {
  const folderName = target.mode === "folder" ? target.folder.folder_name : target.file.folder_name;
  const topType = target.mode === "folder" ? target.folder.top_type : target.file.top_type;
  const label = target.mode === "folder"
    ? `Assign entire folder "${folderName}" (${target.folder.count} files)`
    : `Assign · ${target.file.name}`;
  const [kind, setKind] = useState(topType === "suppliers" ? "supplier" : "client");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (kind === "company") { setRows([]); return; }
    const t = setTimeout(() => {
      if (kind === "client") api("GET", `/clients?q=${encodeURIComponent(q)}&limit=40`).then(setRows).catch(() => setRows([]));
      else api("GET", "/suppliers").then((r) => setRows((r || []).filter((s) => !q || (s.name || "").toLowerCase().includes(q.toLowerCase())))).catch(() => setRows([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, kind]);

  const doAssign = async (payload, ownerLabel) => {
    setBusy(true);
    try {
      if (target.mode === "folder") {
        const r = await api("POST", "/unmatched-files/assign-folder",
          { folder_name: folderName, top_type: topType, ...payload });
        toast.success(`Filed ${r.assigned} files → ${ownerLabel}`);
      } else {
        await api("POST", `/unmatched-files/${target.file.id}/assign`, payload);
        toast.success(`Assigned to ${ownerLabel}`);
      }
      onAssigned();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const KindBtn = ({ k, icon: Icon, text, tid }) => (
    <button onClick={() => setKind(k)} data-testid={tid}
      className={`flex-1 text-[12px] font-bold px-2 py-1.5 rounded inline-flex items-center justify-center gap-1 ${kind === k ? "bg-[#123a63] text-white" : "bg-slate-100 text-slate-600"}`}>
      <Icon className="h-3.5 w-3.5" />{text}</button>
  );

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="assign-picker">
      <div className="w-full max-w-lg bg-white rounded-lg shadow-2xl mt-12 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-[#123a63] text-white">
          <h3 className="font-black text-sm truncate">{label}</h3>
          <button onClick={onClose} data-testid="assign-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-3 space-y-2">
          <div className="text-[11px] text-slate-500">From folder: <b className="text-slate-700">{folderName}</b></div>
          <div className="flex gap-1">
            <KindBtn k="client" icon={Users} text="Client" tid="assign-kind-client" />
            <KindBtn k="supplier" icon={Truck} text="Supplier" tid="assign-kind-supplier" />
            <KindBtn k="company" icon={Building2} text="Company Docs" tid="assign-kind-company" />
          </div>
          {kind === "company" ? (
            <div className="pt-1">
              <p className="text-[12px] text-slate-600 mb-2">File {target.mode === "folder" ? `all ${target.folder.count} files` : "this file"} into your own <b>Company Documents</b> (internal / your business).</p>
              <button disabled={busy} data-testid="assign-company-confirm" onClick={() => doAssign({ kind: "company" }, "Company Documents")}
                className="w-full py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-black">
                {busy ? "Filing…" : "File into Company Documents"}</button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 border border-slate-200 rounded px-2">
                <Search className="h-4 w-4 text-slate-400" />
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${kind}s…`} className="flex-1 text-sm outline-none py-1.5" data-testid="assign-search" />
              </div>
              <div className="border border-slate-200 rounded divide-y divide-slate-100 max-h-72 overflow-y-auto">
                {rows.length === 0 && <p className="p-3 text-slate-400 text-sm">No matches.</p>}
                {rows.map((r) => (
                  <button key={r.id} disabled={busy} onClick={() => doAssign({ kind, owner_id: r.id }, r.name || r.display_name)} data-testid="assign-row" className="w-full flex items-center justify-between px-3 py-2 text-left text-[12px] hover:bg-sky-50">
                    <span className="min-w-0">
                      <span className="font-bold text-[#123a63] truncate block">{r.display_name || r.name}</span>
                      {kind === "client" && <span className="text-slate-500">{[r.legacy_id && `#${r.legacy_id}`, r.phone].filter(Boolean).join(" · ")}</span>}
                    </span>
                    <span className="text-[11px] font-bold text-emerald-700 shrink-0">Assign →</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function FilesReview() {
  const [status, setStatus] = useState(null);
  const [view, setView] = useState("folders"); // 'folders' | 'files'
  const [folders, setFolders] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [topType, setTopType] = useState("");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [assignTarget, setAssignTarget] = useState(null);
  const LIMIT = 60;

  const loadStatus = useCallback(() => { api("GET", "/dropbox-import/status").then(setStatus).catch(() => {}); }, []);
  const loadFolders = useCallback(() => {
    setLoading(true);
    api("GET", `/unmatched-files/folders?q=${encodeURIComponent(q)}&top_type=${topType}`)
      .then((r) => setFolders(r || [])).catch(() => {}).finally(() => setLoading(false));
  }, [q, topType]);
  const loadFiles = useCallback(() => {
    setLoading(true);
    api("GET", `/unmatched-files?q=${encodeURIComponent(q)}&top_type=${topType}&skip=${skip}&limit=${LIMIT}`)
      .then((r) => {
        const t = r.total || 0;
        if (skip > 0 && skip >= t) { setSkip(Math.max(0, (Math.ceil(t / LIMIT) - 1) * LIMIT)); return; }
        setItems(r.items || []); setTotal(t);
      }).catch(() => {}).finally(() => setLoading(false));
  }, [q, topType, skip]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => {
    const t = setTimeout(() => { view === "folders" ? loadFolders() : loadFiles(); }, 250);
    return () => clearTimeout(t);
  }, [view, loadFolders, loadFiles]);
  useEffect(() => {
    if (status && status.running) { const t = setInterval(loadStatus, 5000); return () => clearInterval(t); }
  }, [status, loadStatus]);

  const reloadAll = () => { loadStatus(); view === "folders" ? loadFolders() : loadFiles(); };
  const remove = async (f) => {
    if (!window.confirm(`Permanently delete "${f.name}"? This cannot be undone.`)) return;
    try { await api("DELETE", `/unmatched-files/${f.id}`); toast.success("Deleted"); reloadAll(); }
    catch (e) { toast.error(e.message); }
  };
  const onAssigned = () => { setAssignTarget(null); reloadAll(); };
  const deleteFolder = async (f) => {
    if (!window.confirm(`Permanently delete ALL ${f.count} files in "${f.folder_name}"? This cannot be undone.`)) return;
    try { const r = await api("POST", "/unmatched-files/delete-folder", { folder_name: f.folder_name, top_type: f.top_type }); toast.success(`Deleted ${r.deleted} files`); reloadAll(); }
    catch (e) { toast.error(e.message); }
  };
  const confirmSuggestion = async (f) => {
    const s = f.suggestion; if (!s) return;
    try {
      const r = await api("POST", "/unmatched-files/assign-folder",
        { folder_name: f.folder_name, top_type: f.top_type, kind: s.kind, owner_id: s.owner_id, owner_name: s.owner_name });
      toast.success(`Filed ${r.assigned} files → ${s.kind === "new_supplier" ? f.folder_name + " (new supplier)" : s.owner_name}`);
      reloadAll();
    } catch (e) { toast.error(e.message); }
  };
  const [bulkBusy, setBulkBusy] = useState(false);
  const confirmAllConfident = async () => {
    const targets = folders.filter((f) => f.suggestion && ["high", "medium"].includes(f.suggestion.confidence));
    if (!targets.length) { toast.info("No high/medium-confidence suggestions to confirm."); return; }
    if (!window.confirm(`Auto-file ${targets.length} folders with high/medium-confidence matches? Low-confidence and "create supplier" folders are left for you to review.`)) return;
    setBulkBusy(true);
    let ok = 0;
    for (const f of targets) {
      const s = f.suggestion;
      try { await api("POST", "/unmatched-files/assign-folder", { folder_name: f.folder_name, top_type: f.top_type, kind: s.kind, owner_id: s.owner_id, owner_name: s.owner_name }); ok++; }
      catch (e) { /* keep going */ }
    }
    setBulkBusy(false);
    toast.success(`Auto-filed ${ok} folders`); reloadAll();
  };

  const imported = status?.imported ?? 0;
  const totalFiles = status?.total_files ?? 0;
  const running = status?.running;
  const pct = totalFiles ? Math.round((imported / totalFiles) * 100) : 0;

  return (
    <div className="p-4 max-w-6xl mx-auto" data-testid="files-review">
      <div className="flex items-center gap-2 mb-3">
        <Inbox className="h-6 w-6 text-[#123a63]" />
        <h2 className="text-lg font-black text-[#123a63]">Files Review — Unassigned Attachments</h2>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4 text-center text-[12px]">
        <div className="bg-white border border-slate-200 rounded p-2" data-testid="stat-imported">
          <div className="text-slate-500">Imported</div>
          <div className="font-black text-slate-800">{imported.toLocaleString()}<span className="text-slate-400 font-normal"> / {totalFiles.toLocaleString()}</span></div>
        </div>
        <div className="bg-white border border-slate-200 rounded p-2">
          <div className="text-slate-500">Client files</div>
          <div className="font-black text-emerald-700" data-testid="stat-client-files">{status ? status.linked_client_files.toLocaleString() : "—"}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded p-2">
          <div className="text-slate-500">Supplier files</div>
          <div className="font-black text-sky-700" data-testid="stat-supplier-files">{status ? status.supplier_files.toLocaleString() : "—"}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded p-2">
          <div className="text-slate-500">To review</div>
          <div className="font-black text-amber-600" data-testid="stat-review">{status ? status.review_files.toLocaleString() : "—"}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded p-2">
          <div className="text-slate-500">Status</div>
          <div className={`font-black ${running ? "text-amber-600" : "text-emerald-700"}`} data-testid="stat-state">{running ? "Importing…" : "Complete"}</div>
        </div>
      </div>
      {running && (
        <div className="mb-4" data-testid="import-progress">
          <div className="h-2 bg-slate-200 rounded overflow-hidden"><div className="h-full bg-[#123a63] transition-all" style={{ width: `${pct}%` }} /></div>
          <div className="text-[11px] text-slate-500 mt-1">Import running… {pct}% ({imported.toLocaleString()} of {totalFiles.toLocaleString()}) — refreshes automatically.</div>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="inline-flex rounded overflow-hidden border border-slate-200" data-testid="review-view-toggle">
          <button onClick={() => setView("folders")} data-testid="review-view-folders" className={`px-3 py-1.5 text-[12px] font-bold inline-flex items-center gap-1 ${view === "folders" ? "bg-[#123a63] text-white" : "bg-white text-slate-600"}`}><FolderClosed className="h-3.5 w-3.5" />By Folder</button>
          <button onClick={() => { setSkip(0); setView("files"); }} data-testid="review-view-files" className={`px-3 py-1.5 text-[12px] font-bold inline-flex items-center gap-1 ${view === "files" ? "bg-[#123a63] text-white" : "bg-white text-slate-600"}`}><FileText className="h-3.5 w-3.5" />All Files</button>
        </div>
        <div className="flex items-center gap-2 border border-slate-200 rounded px-2 bg-white flex-1 min-w-[220px]">
          <Search className="h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => { setSkip(0); setQ(e.target.value); }} placeholder={view === "folders" ? "Search folders…" : "Search folder or file name…"} className="flex-1 text-sm outline-none py-1.5" data-testid="review-search" />
        </div>
        <select value={topType} onChange={(e) => { setSkip(0); setTopType(e.target.value); }} className={`${cellInp} w-auto`} data-testid="review-filter-type">
          <option value="">All sources</option>
          <option value="clients">From /Clients</option>
          <option value="suppliers">From /Product Suppliers</option>
        </select>
      </div>

      {/* FOLDER VIEW */}
      {view === "folders" && (
        <div className="space-y-2" data-testid="review-folder-list">
          {loading && <p className="text-slate-400 text-sm">Loading…</p>}
          {!loading && folders.length === 0 && <p className="text-slate-400 text-sm">No folders to review — all files are assigned. 🎉</p>}
          {!loading && folders.length > 0 && folders.some((f) => f.suggestion && ["high", "medium"].includes(f.suggestion.confidence)) && (
            <div className="flex items-center justify-between bg-sky-50 border border-sky-200 rounded-lg px-3 py-2 mb-1">
              <span className="text-[12px] text-sky-900 font-semibold">Suggested matches ready — confirm the confident ones in one go.</span>
              <button onClick={confirmAllConfident} disabled={bulkBusy} data-testid="confirm-all-confident"
                className="px-3 py-1.5 rounded bg-[#123a63] hover:bg-[#1a4f8a] text-white text-[12px] font-black disabled:opacity-50">{bulkBusy ? "Filing…" : "Confirm confident matches"}</button>
            </div>
          )}
          {!loading && folders.map((f) => {
            const s = f.suggestion;
            const chip = s && {
              high: "bg-emerald-100 text-emerald-800 border-emerald-200",
              medium: "bg-sky-100 text-sky-800 border-sky-200",
              low: "bg-amber-100 text-amber-800 border-amber-200",
              new: "bg-violet-100 text-violet-800 border-violet-200",
            }[s.confidence];
            const sugLabel = s && (s.kind === "new_supplier" ? `Create supplier "${f.folder_name}"`
              : s.kind === "company" ? "Company Documents"
              : `${s.kind === "client" ? "Client" : "Supplier"}: ${s.owner_name}`);
            return (
            <div key={`${f.top_type}:${f.folder_name}`} className="flex items-center gap-3 border border-slate-200 rounded-lg bg-white p-2" data-testid="review-folder-row">
              <div className="h-12 w-12 shrink-0 rounded bg-slate-50 flex items-center justify-center overflow-hidden">
                {isImg(f.sample_ct) ? <img src={`${base}${f.sample_url}`} alt="" className="h-full w-full object-cover" /> : <FolderClosed className="h-6 w-6 text-slate-400" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-[#123a63] truncate">{f.folder_name}</p>
                <p className="text-[11px] text-slate-500">{f.count.toLocaleString()} file{f.count === 1 ? "" : "s"} · {f.top_type === "suppliers" ? "Product Suppliers" : "Clients"}</p>
                {s && (
                  <div className="mt-1 flex items-center gap-1.5" data-testid="folder-suggestion">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border ${chip}`}><Sparkles className="h-3 w-3" />{s.confidence === "new" ? "suggest" : s.confidence}</span>
                    <span className="text-[11px] text-slate-600 truncate">{sugLabel}</span>
                  </div>
                )}
              </div>
              {s && (
                <button onClick={() => confirmSuggestion(f)} data-testid="folder-confirm"
                  className="shrink-0 px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-black">Confirm</button>
              )}
              <button onClick={() => setAssignTarget({ mode: "folder", folder: f })} data-testid="folder-assign" title="Assign manually"
                className="shrink-0 px-3 py-1.5 rounded border border-slate-300 text-slate-600 hover:bg-slate-50 text-[12px] font-bold">{s ? "Other…" : "Assign →"}</button>
              <button onClick={() => deleteFolder(f)} data-testid="folder-delete" title="Delete entire folder"
                className="shrink-0 p-1.5 rounded hover:bg-rose-50"><Trash2 className="h-4 w-4 text-slate-300 hover:text-rose-600" /></button>
            </div>
          );})}
        </div>
      )}

      {/* FILE VIEW */}
      {view === "files" && (
        <>
          <div className="text-[12px] text-slate-500 mb-2" data-testid="review-count">{total.toLocaleString()} file{total === 1 ? "" : "s"}</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {loading && <p className="col-span-full text-slate-400 text-sm">Loading…</p>}
            {!loading && items.length === 0 && <p className="col-span-full text-slate-400 text-sm">Nothing to review here. 🎉</p>}
            {!loading && items.map((f) => (
              <div key={f.id} className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid="review-file-card">
                <a href={`${base}${f.url}`} target="_blank" rel="noreferrer" className="block h-24 bg-slate-50 flex items-center justify-center">
                  {isImg(f.content_type) ? <img src={`${base}${f.url}`} alt={f.name} className="h-full w-full object-cover" /> : <FileText className="h-9 w-9 text-slate-400" />}
                </a>
                <div className="p-2">
                  <p className="text-[11px] font-semibold text-slate-700 truncate" title={f.name}>{f.name}</p>
                  <p className="text-[10px] text-slate-400 truncate" title={f.folder_name}>{f.folder_name}</p>
                  <div className="flex justify-between items-center mt-1.5 gap-1">
                    <button onClick={() => setAssignTarget({ mode: "file", file: f })} data-testid="review-assign" className="text-[11px] font-bold text-emerald-700 hover:underline">Assign</button>
                    <div className="flex items-center gap-2">
                      <a href={`${base}${f.url}`} target="_blank" rel="noreferrer" className="text-sky-700" title="Open"><Download className="h-3.5 w-3.5" /></a>
                      <button onClick={() => remove(f)} data-testid="review-delete" title="Delete"><Trash2 className="h-3.5 w-3.5 text-slate-300 hover:text-rose-600" /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {total > LIMIT && (
            <div className="flex justify-center items-center gap-3 mt-4">
              <HdrBtn testid="review-prev" onClick={() => setSkip(Math.max(0, skip - LIMIT))} className={skip === 0 ? "opacity-40 pointer-events-none" : ""}>← Prev</HdrBtn>
              <span className="text-[12px] text-slate-500">{skip + 1}–{Math.min(skip + LIMIT, total)} of {total.toLocaleString()}</span>
              <HdrBtn testid="review-next" onClick={() => setSkip(skip + LIMIT)} className={skip + LIMIT >= total ? "opacity-40 pointer-events-none" : ""}>Next →</HdrBtn>
            </div>
          )}
        </>
      )}

      {assignTarget && <AssignPicker target={assignTarget} onAssigned={onAssigned} onClose={() => setAssignTarget(null)} />}
    </div>
  );
}
